<template>
  <div>

    <div class="grid grid-cols-2 max-w-sm mt-1">
      <label class="flex items-center select-none">
        <input
          v-model="hasWatermark"
          class="text-blue-450 focus:ring-0"
          type="checkbox"
          name="options"
          checked
        >
        <span
          class="ml-2 text-xs font-semibold"
          :class="[opacityClass]"
        >
          Watermark
        </span>
      </label>

      <input-file
        v-model:watermarkImage="watermarkImageProxy"
        name="watermark-image"
        :model="watermarkImage"
        :default="ffmpegData.watermark_image_url"
        class="bg-gray-100 max-h-32"
        :class="[opacityClass]"
        :key="imageKey"
      />
    </div>

    <div class="grid grid-cols-2 max-w-sm mt-3">
      <span
        :class="[opacityClass]"
        class="text-xs font-semibold text-gray-victor"
      >
        Position
      </span>

      <div
        :class="[opacityClass]"
        class="p-2 py-1 bg-gray-lightGrey"
      >
        <div
          v-for="(chunk,index) in watermarkPositions"
          :key="index"
          class="flex justify-around my-1"
        >
          <input
            v-for="(item, itemIndex) in chunk"
            :key="itemIndex"
            v-model="watermarkPosition"
            :class="[borderClass]"
            type="radio"
            name="watermark_position"
            :disabled="!hasWatermark"
            :value="item"
            class="text-blue-450 focus:ring-0 focus:border-0 w-3.5 h-3.5 border-gray-300 appearance-none checked:bg-blue-450 focus:shadow-none focus:ring-offset-0"
          >
        </div>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-2 max-w-sm mt-3" :class="[opacityClass]">
      <label class="text-xs font-semibold text-gray-victor">Horizontal Margin (px)</label>
      <input
        type="number"
        min="0"
        v-model="watermarkHorizontalM"
        class="bg-gray-100 border-0 rounded px-2 py-1 text-xs"
        :disabled="!hasWatermark"
      />

      <label class="text-xs font-semibold text-gray-victor">Vertical Margin (px)</label>
      <input
        type="number"
        min="0"
        v-model="watermarkVerticalM"
        class="bg-gray-100 border-0 rounded px-2 py-1 text-xs"
        :disabled="!hasWatermark"
      />

      <label class="text-xs font-semibold text-gray-victor">Height (px)</label>
      <input
        type="number"
        min="0"
        max="100"
        v-model="watermarkScaleH"
        class="bg-gray-100 border-0 rounded px-2 py-1 text-xs"
        :disabled="!hasWatermark"
      />

      <label class="text-xs font-semibold text-gray-victor">Width (px)</label>
      <input
        type="number"
        min="0"
        max="100"
        v-model="watermarkScaleW"
        class="bg-gray-100 border-0 rounded px-2 py-1 text-xs"
        :disabled="!hasWatermark"
      />
    </div>

    <div class="w-full max-w-sm mt-3">
      <span
        :class="[opacityClass]"
        class="text-xs font-semibold select-none text-gray-victor"
      >
        Appearance
      </span>

    <div class="mt-3">
      <AppearanceRange
        v-model:model="watermarkInterval"
        v-model:active="hasWatermark"
        :duration="ffmpegData.video_duration"
        :previewCurrentTime="previewCurrentTime"
        :getPreviewCurrentTime="getPreviewCurrentTime"
      />
    </div>
    </div>
  </div>
</template>

<script>
import inputFile from "../form/inputFile"
import AppearanceRange from "./AppearanceRange"

export default {
  name: 'WatermarkTab',
  components: {
    inputFile,
    AppearanceRange
  },
  data() {
    return {
      imageKey: 1
    }
  },
  props: {
    ffmpegData: {
      type: Object,
      required: true
    },
    watermarkImage: {
      type: [File, String, Object, null],
      default () {
        return null
      }
    },
    watermarkPositions: {
      type: Array,
      required: true
    },
    previewCurrentTime: {
      type: Number,
      default () {
        return 0
      }
    },
    getPreviewCurrentTime: {
      type: Function,
      default () {
        return null
      }
    },
    opacityClass: {
      type: String,
      default () {
        return ''
      }
    },
    borderClass: {
      type: String,
      default () {
        return ''
      }
    }
  },
  emits: ['update:watermarkImage', 'update:ffmpegData'],
  computed: {
    watermarkImageProxy: {
      get () {
        return this.watermarkImage
      },
      set (val) {
        this.$emit('update:watermarkImage', val)
      }
    },
    hasWatermark: {
      get () {
        return this.ffmpegData.has_watermark
      },
      set (val) {
        this.updateFfmpegField('has_watermark', val)
      }
    },
    watermarkPosition: {
      get () {
        return this.ffmpegData.watermark_position
      },
      set (val) {
        this.updateFfmpegField('watermark_position', val)
      }
    },
    watermarkHorizontalM: {
      get () {
        return this.ffmpegData.watermark_horizontal_m
      },
      set (val) {
        this.updateFfmpegField('watermark_horizontal_m', val)
      }
    },
    watermarkVerticalM: {
      get () {
        return this.ffmpegData.watermark_vertical_m
      },
      set (val) {
        this.updateFfmpegField('watermark_vertical_m', val)
      }
    },
    watermarkScaleH: {
      get () {
        return this.ffmpegData.watermark_scale_h
      },
      set (val) {
        this.updateFfmpegField('watermark_scale_h', val)
      }
    },
    watermarkScaleW: {
      get () {
        return this.ffmpegData.watermark_scale_w
      },
      set (val) {
        this.updateFfmpegField('watermark_scale_w', val)
      }
    },
    watermarkInterval: {
      get () {
        return this.ffmpegData.watermark_interval
      },
      set (val) {
        this.updateFfmpegField('watermark_interval', val)
      }
    }
  },
  methods: {
    updateFfmpegField (field, value) {
      this.$emit('update:ffmpegData', {
        ...this.ffmpegData,
        [field]: value
      })
    }
  },
  watch: {
    'watermarkImage': {
      handler (val) {
        if(typeof val === 'string') this.imageKey++
      }
    }
  }
}
</script>
